// src/services/rankingService.js
// Custom Rankings (docs/custom-rankings.md). Owner routes need a signed-in
// user and see the private working version (drafts included); title search,
// the public share page (published version only) and the id-only preview card
// work for guests.
import { apiRequest, apiBlob, API_BASE_URL } from './api';

const enc = encodeURIComponent;

export const RankingService = {
    /** Movies and / or TV. kind: 'movie' | 'tv' | undefined (both). → { results } */
    searchTitles(query, { kind, page = 1 } = {}) {
        return apiRequest('/media/search', { params: { q: query, page, kind: kind || undefined }, authenticated: false });
    },

    /** New private draft (may be incomplete). → { ranking, created } (created false when this draft was already saved) */
    create(body) {
        return apiRequest('/rankings', { method: 'POST', body });
    },

    /** Own rankings, newest first; status 'draft' | 'published' | undefined (both). → { items, nextCursor, hasMore } */
    listMine({ cursor, limit, status } = {}) {
        return apiRequest('/rankings/me', { params: { cursor, limit, status } });
    },

    /** → { ranking } */
    getMine(id) {
        return apiRequest(`/rankings/me/${enc(id)}`);
    },

    /** Save the working version (private; drafts may be incomplete): { title, description, size, items, showPositions } → { ranking } */
    replace(id, body) {
        return apiRequest(`/rankings/me/${enc(id)}`, { method: 'PUT', body });
    },

    /** { title } / { description } / { showPositions } on the working version → { ranking } */
    update(id, body) {
        return apiRequest(`/rankings/me/${enc(id)}`, { method: 'PATCH', body });
    },

    /**
     * Publish, or update the published ranking. With `body` (the editor's
     * content) it is saved and published in one step; refused with nothing
     * written when it can't be published. → { ranking }
     */
    publish(id, body) {
        return apiRequest(`/rankings/me/${enc(id)}/publish`, { method: 'POST', body: body || {} });
    },

    /** Back to a private draft; the link stops working, the content and URL are kept. → { ranking } */
    unpublish(id) {
        return apiRequest(`/rankings/me/${enc(id)}/unpublish`, { method: 'POST', body: {} });
    },

    /** Throw away unpublished changes: back to the published version. → { ranking } */
    discardChanges(id) {
        return apiRequest(`/rankings/me/${enc(id)}/discard`, { method: 'POST', body: {} });
    },

    /**
     * The owner's private preview image of their working version (drafts
     * included), fetched with their session — there is no public URL for it.
     * `query` is rankingCardQuery(…). → Blob (image/png)
     */
    ownCardBlob(id, { query }) {
        return apiBlob(`/rankings/me/${enc(id)}/card.png?${query}`);
    },

    remove(id) {
        return apiRequest(`/rankings/me/${enc(id)}`, { method: 'DELETE' });
    },

    /**
     * Another user's PUBLIC rankings, newest first, only when their profile is
     * viewable (same rule as the profile). → { user, isPrivate, items, nextCursor, hasMore }
     */
    listForUser(username, { cursor, limit } = {}) {
        return apiRequest(`/users/${enc(username)}/rankings`, { params: { cursor, limit } });
    },

    /** Anyone-with-the-link view. → { isPrivate } or { ranking, owner, items, shareUrl, cardUrl, attribution } */
    getShared(slug) {
        return apiRequest(`/share/rankings/${enc(slug)}`, { authenticated: false });
    },

    /**
     * Image for an unsaved / guest / private ranking: ids only, no title or
     * username (rendered without user text). `query` is rankingCardQuery(…,
     * { personal: false }) and wins over `format`.
     */
    previewCardUrl({ items, size, format = 'square', query = null }) {
        const ids = items.map((item) => `${item.kind}:${item.tmdbId}`).join(',');
        return `${API_BASE_URL}/rankings/card.png?items=${enc(ids)}&size=${size}&${query || `format=${format}`}`;
    },

    /** Image for a saved, public ranking (with its title and @username). `query` is rankingCardQuery(…). */
    sharedCardUrl(slug, { format = 'square', query = null } = {}) {
        return `${API_BASE_URL}/share/rankings/${enc(slug)}/card.png?${query || `format=${format}`}`;
    },
};

/** User-facing copy for a failed request; keeps the server's validation message. */
export function getRankingErrorMessage(err, fallback) {
    if (err?.isNetworkError) return err.message;
    if (err?.statusCode === 429 && err.message) return err.message;
    if (err?.statusCode >= 400 && err?.statusCode < 500 && err.statusCode !== 401 && err.message && err.message !== 'Request failed') return err.message;
    return fallback;
}
